import { useEffect, useMemo, useRef, useState } from "react";
import { usePageMeta } from "@/components/pepmuse/usePageMeta";
import { AnimatePresence, motion } from "framer-motion";
import {
  Check,
  ChevronDown,
  Download,
  Palette,
  FileCheck,
  Globe,
  Package,
  CreditCard,
  Megaphone,
  PartyPopper,
  RotateCcw,
} from "lucide-react";
import { SubpageHero } from "@/components/pepmuse/SubpageHero";
import { ClosingCTA } from "@/components/pepmuse/ClosingCTA";
import { Reveal, Eyebrow } from "@/components/pepmuse/Reveal";
import { PrimaryButton } from "@/components/pepmuse/Buttons";
import { scrollToId } from "@/components/pepmuse/scroll";
import { CHECKLIST_CATEGORIES, ALL_ITEMS, MIN_PRICE, MAX_PRICE } from "@/components/pepmuse/pricing";
import { downloadProposalPdf } from "@/components/pepmuse/pdf";

const CATEGORY_ICONS = {
  branding: Palette,
  legal: FileCheck,
  website: Globe,
  product: Package,
  payment: CreditCard,
  marketing: Megaphone,
};

// One accent color per category — used on chips, checkboxes, progress segments,
// and proposal groups so the whole tool reads at a glance.
const CATEGORY_COLORS = {
  branding: "#B8A7E8",
  legal: "#8FBFE8",
  website: "#8FD8B8",
  product: "#F0B28A",
  payment: "#E8A7C0",
  marketing: "#E8CE8F",
};

const money = (n) => `$${n.toLocaleString("en-US")}`;

const fieldClass =
  "w-full rounded-xl border border-white/10 bg-ink/70 px-4 py-3 text-sm text-paper placeholder:text-paper/35 transition-[border-color,box-shadow] duration-200 focus:border-lilac/60 focus:outline-none focus:ring-2 focus:ring-lilac/30";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STORAGE_KEY = "pepmuse-checklist-v1";

const loadSaved = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || null;
  } catch {
    return null;
  }
};

const ChecklistItem = ({ item, checked, onToggle, color }) => (
  <label className="flex cursor-pointer items-start gap-4 rounded-xl px-3 py-3.5 transition-colors duration-200 hover:bg-white/[0.03] sm:px-4">
    <input
      type="checkbox"
      data-testid={`checklist-item-${item.id}`}
      checked={checked}
      onChange={onToggle}
      className="peer sr-only"
    />
    <span
      aria-hidden="true"
      style={checked ? { backgroundColor: color, borderColor: color } : undefined}
      className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-white/20 text-ink transition-[background-color,border-color,transform] duration-200 peer-focus-visible:ring-2 peer-focus-visible:ring-lilac peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-card peer-active:scale-95"
    >
      <Check
        className={`h-4 w-4 transition-opacity duration-150 ${checked ? "opacity-100" : "opacity-0"}`}
        strokeWidth={3.5}
      />
    </span>
    <span>
      <span className={`block text-sm font-bold transition-colors duration-200 ${checked ? "text-paper/45 line-through decoration-paper/30" : "text-paper"}`}>
        {item.question}
      </span>
      <span className="mt-0.5 block text-xs text-paper/45">{item.help}</span>
    </span>
  </label>
);

export default function LaunchChecklist({ onBuild }) {
  const saved = useRef(loadSaved()).current;
  const [checked, setChecked] = useState(saved?.checked ?? {});
  const [openCats, setOpenCats] = useState({ branding: true });
  const [generated, setGenerated] = useState(false);
  const [contact, setContact] = useState(
    saved?.contact ?? { name: "", email: "", phone: "", businessName: "" }
  );
  const [contactErrors, setContactErrors] = useState({});
  const [panelVisible, setPanelVisible] = useState(true);
  const numberRef = useRef(null);

  usePageMeta(
    "Launch Checklist — PepMuse",
    "Plan your research peptide brand launch step by step and get a personalized, priced proposal PDF."
  );

  // Remember checklist progress + contact details between visits.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ checked, contact }));
    } catch {
      /* storage unavailable */
    }
  }, [checked, contact]);

  // Slim progress bar pins under the header once the readiness panel scrolls away.
  useEffect(() => {
    const panel = document.querySelector('[data-testid="readiness-panel"]');
    if (!panel) return undefined;
    const obs = new IntersectionObserver(([entry]) => setPanelVisible(entry.isIntersecting), {
      rootMargin: "-76px 0px 0px 0px",
    });
    obs.observe(panel);
    return () => obs.disconnect();
  }, []);

  const total = ALL_ITEMS.length;
  const done = ALL_ITEMS.filter((i) => checked[i.id]).length;

  const toggleItem = (id) => setChecked((c) => ({ ...c, [id]: !c[id] }));
  const toggleCat = (key) => setOpenCats((o) => ({ ...o, [key]: !o[key] }));
  const setContactField = (key) => (e) => {
    setContact((c) => ({ ...c, [key]: e.target.value }));
    setContactErrors((err) => ({ ...err, [key]: undefined }));
  };

  // Live estimate — moves as boxes are checked, before and after generating.
  const estimate = useMemo(() => {
    const needed = ALL_ITEMS.filter((i) => !checked[i.id]);
    const subtotal = needed.reduce((s, i) => s + i.price, 0);
    const totalPrice = needed.length === 0 ? 0 : Math.min(MAX_PRICE, Math.max(MIN_PRICE, subtotal));
    return {
      needed,
      subtotal,
      totalPrice,
      discount: subtotal > totalPrice ? subtotal - totalPrice : 0,
      minimumApplied: needed.length > 0 && subtotal < totalPrice,
    };
  }, [checked]);

  const proposal = useMemo(() => {
    if (!generated) return null;
    return {
      ...estimate,
      groups: CHECKLIST_CATEGORIES.map((c) => ({
        ...c,
        neededItems: c.items.filter((i) => !checked[i.id]),
      })).filter((g) => g.neededItems.length > 0),
      date: new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
    };
  }, [checked, generated, estimate]);

  const generate = () => {
    // Name and email are required before a proposal is generated.
    const next = {};
    if (!contact.name.trim()) next.name = "Please tell us your name.";
    if (!contact.email.trim()) next.email = "Please enter your email address.";
    else if (!EMAIL_RE.test(contact.email.trim())) next.email = "That email doesn't look right — mind checking it?";
    setContactErrors(next);
    const firstError = next.name ? "contact-name" : next.email ? "contact-email" : null;
    if (firstError) {
      scrollToId("checklist");
      setTimeout(() => document.getElementById(firstError)?.focus(), 60);
      return;
    }
    if (!numberRef.current) numberRef.current = Math.floor(Math.random() * 9000) + 1000;
    setGenerated(true);
    setTimeout(() => scrollToId("proposal"), 90);
  };

  const reset = () => {
    setChecked({});
    setGenerated(false);
    setContact({ name: "", email: "", phone: "", businessName: "" });
    setContactErrors({});
    numberRef.current = null;
    scrollToId("checklist");
  };

  const contactPrefill = {
    ...(contact.name.trim() ? { name: contact.name.trim() } : {}),
    ...(contact.email.trim() ? { email: contact.email.trim() } : {}),
  };

  const openInquiryWithProposal = () => {
    const details = [
      `Launch checklist proposal #${numberRef.current} — estimated investment ${money(
        estimate.totalPrice
      )} with ${estimate.needed.length} of ${total} items remaining.`,
      contact.businessName.trim() ? `Business: ${contact.businessName.trim()}.` : "",
      contact.phone.trim() ? `Phone: ${contact.phone.trim()}.` : "",
    ]
      .filter(Boolean)
      .join(" ");
    onBuild({ need: "Launch Checklist", idea: details, ...contactPrefill });
  };

  return (
    <>
      <SubpageHero
        testId="checklist-hero"
        eyebrow="Launch checklist · Free tool"
        title="How ready is your brand?"
        body="Tick off what you already have in place. We'll show you exactly what's left, what it costs, and how we can bring it together."
        onBuild={onBuild}
        secondary={{ label: "Browse the checklist", target: "checklist" }}
        note="Watch the estimate move as you check — name and email are only needed to generate."
      />

      <AnimatePresence>
        {!panelVisible && (
          <motion.div
            data-testid="mini-progress-bar"
            initial={{ y: -72, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -72, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="fixed inset-x-0 top-[72px] z-30 border-b border-white/10 bg-ink/90 backdrop-blur-md"
          >
            <div className="mx-auto flex h-14 w-full max-w-shell items-center gap-3 px-5 sm:gap-4 sm:px-8">
              <span data-testid="mini-progress-count" className="whitespace-nowrap text-xs font-bold text-paper/60">
                {done} of {total}
              </span>
              <div className="flex h-1.5 min-w-0 flex-1 gap-0.5">
                {CHECKLIST_CATEGORIES.map((cat) => {
                  const catDone = cat.items.filter((i) => checked[i.id]).length;
                  return (
                    <div
                      key={cat.key}
                      className="relative h-full overflow-hidden rounded-full bg-white/[0.08]"
                      style={{ width: `${(cat.items.length / total) * 100}%` }}
                    >
                      <div
                        className="h-full rounded-full transition-[width] duration-300"
                        style={{
                          width: `${(catDone / cat.items.length) * 100}%`,
                          backgroundColor: CATEGORY_COLORS[cat.key],
                        }}
                      />
                    </div>
                  );
                })}
              </div>
              <span data-testid="mini-progress-estimate" className="whitespace-nowrap font-display text-sm font-extrabold text-lilac">
                {done === total ? "$0" : money(estimate.totalPrice)}
              </span>
              <button
                type="button"
                data-testid="mini-progress-generate"
                onClick={() => (generated ? scrollToId("proposal") : generate())}
                className="whitespace-nowrap rounded-full bg-lilac px-4 py-2 text-xs font-bold text-ink transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-lilac-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac focus-visible:ring-offset-2 focus-visible:ring-offset-ink active:translate-y-0"
              >
                {generated ? "View proposal" : "Generate"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <section id="checklist" data-testid="checklist-section" className="scroll-mt-24 pb-16 pt-4 sm:pb-20" tabIndex={-1}>
        <div className="mx-auto w-full max-w-shell px-5 sm:px-8">
          <Reveal>
            <div
              data-testid="readiness-panel"
              className="rounded-[2rem] border border-lilac/25 bg-gradient-to-b from-lilac/[0.09] to-card p-7 sm:p-9"
            >
              <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="text-xs font-bold uppercase tracking-[0.22em] text-paper/45">Your readiness</p>
                    <p data-testid="checklist-progress-text" className="text-sm font-bold text-paper/70">
                      {done} of {total} in place
                    </p>
                  </div>
                  <div className="mt-4 flex h-3 w-full gap-1">
                    {CHECKLIST_CATEGORIES.map((cat) => {
                      const catDone = cat.items.filter((i) => checked[i.id]).length;
                      return (
                        <div
                          key={cat.key}
                          title={cat.label}
                          className="relative h-full overflow-hidden rounded-full bg-white/[0.08]"
                          style={{ width: `${(cat.items.length / total) * 100}%` }}
                        >
                          <div
                            data-testid={`progress-segment-${cat.key}`}
                            className="h-full rounded-full transition-[width] duration-300 ease-out"
                            style={{
                              width: `${(catDone / cat.items.length) * 100}%`,
                              backgroundColor: CATEGORY_COLORS[cat.key],
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
                    {CHECKLIST_CATEGORIES.map((cat) => (
                      <span key={cat.key} className="flex items-center gap-2 text-xs font-bold text-paper/50">
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ backgroundColor: CATEGORY_COLORS[cat.key] }}
                          aria-hidden="true"
                        />
                        {cat.label}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="shrink-0 lg:text-right">
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-paper/45">
                    Estimated investment for what's left
                  </p>
                  <p
                    data-testid="live-estimate"
                    className="mt-2 font-display text-5xl font-extrabold tracking-tight text-lilac sm:text-6xl"
                  >
                    {done === total ? "$0" : money(estimate.totalPrice)}
                  </p>
                  <p className="mt-1.5 text-sm text-paper/50">
                    {estimate.needed.length === 0
                      ? "Nothing left — you're launch-ready."
                      : `${estimate.needed.length} ${
                          estimate.needed.length === 1 ? "item" : "items"
                        } remaining before launch`}
                  </p>
                </div>
              </div>

              <div className="mt-8 grid gap-4 border-t border-white/[0.07] pt-7 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <label htmlFor="contact-name" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.14em] text-paper/50">
                    Name <span className="text-lilac" aria-hidden="true">*</span>
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    type="text"
                    autoComplete="name"
                    required
                    value={contact.name}
                    onChange={setContactField("name")}
                    aria-invalid={Boolean(contactErrors.name)}
                    aria-describedby={contactErrors.name ? "contact-name-error" : undefined}
                    placeholder="Your name"
                    className={fieldClass}
                  />
                  {contactErrors.name && (
                    <p id="contact-name-error" role="alert" data-testid="contact-name-error" className="mt-1.5 text-xs font-semibold text-red-300">
                      {contactErrors.name}
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.14em] text-paper/50">
                    Email <span className="text-lilac" aria-hidden="true">*</span>
                  </label>
                  <input
                    id="contact-email"
                    data-testid="contact-email-input"
                    type="email"
                    autoComplete="email"
                    required
                    value={contact.email}
                    onChange={setContactField("email")}
                    aria-invalid={Boolean(contactErrors.email)}
                    aria-describedby={contactErrors.email ? "contact-email-error" : undefined}
                    placeholder="you@example.com"
                    className={fieldClass}
                  />
                  {contactErrors.email && (
                    <p id="contact-email-error" role="alert" data-testid="contact-email-error" className="mt-1.5 text-xs font-semibold text-red-300">
                      {contactErrors.email}
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor="contact-phone" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.14em] text-paper/50">
                    Phone
                  </label>
                  <input
                    id="contact-phone"
                    data-testid="contact-phone-input"
                    type="tel"
                    autoComplete="tel"
                    value={contact.phone}
                    onChange={setContactField("phone")}
                    placeholder="(555) 000-0000"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label htmlFor="contact-business" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.14em] text-paper/50">
                    Business name
                  </label>
                  <input
                    id="contact-business"
                    data-testid="contact-business-input"
                    type="text"
                    autoComplete="organization"
                    value={contact.businessName}
                    onChange={setContactField("businessName")}
                    placeholder="Your brand"
                    className={fieldClass}
                  />
                </div>
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3">
                <PrimaryButton testId="generate-proposal-button" onClick={generate}>
                  {generated ? "Update my proposal" : "Generate my proposal"}
                </PrimaryButton>
                <p className="text-xs text-paper/40">
                  Name and email are needed to generate — we only use them to personalize and follow up on your
                  proposal.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-16">
              <Eyebrow index="01" testId="checklist-eyebrow">The checklist</Eyebrow>
              <h2 className="mt-4 max-w-xl font-display text-3xl font-extrabold tracking-tight text-paper sm:text-4xl">
                Six areas. Twenty-six boxes.
              </h2>
              <p className="mt-4 max-w-md text-base text-paper/60">
                Check everything you already have — the estimate above moves as you go.
              </p>
            </div>
          </Reveal>

          <div className="mt-10 space-y-4">
            {CHECKLIST_CATEGORIES.map((cat, ci) => {
              const Icon = CATEGORY_ICONS[cat.key];
              const color = CATEGORY_COLORS[cat.key];
              const catDone = cat.items.filter((i) => checked[i.id]).length;
              const isComplete = catDone === cat.items.length;
              const isOpen = Boolean(openCats[cat.key]);
              return (
                <Reveal key={cat.key} delay={ci * 0.05}>
                  <div
                    data-testid={`checklist-category-${cat.key}`}
                    className="overflow-hidden rounded-3xl border bg-card transition-colors duration-300"
                    style={{ borderColor: isComplete ? `${color}59` : "rgba(255,255,255,0.08)" }}
                  >
                    <button
                      type="button"
                      data-testid={`checklist-toggle-${cat.key}`}
                      aria-expanded={isOpen}
                      onClick={() => toggleCat(cat.key)}
                      className="flex w-full items-center gap-4 p-5 text-left transition-colors duration-200 hover:bg-white/[0.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-lilac sm:p-6"
                    >
                      <span
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-ink transition-transform duration-200"
                        style={{ backgroundColor: color }}
                      >
                        <Icon className="h-5 w-5" strokeWidth={2.4} aria-hidden="true" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-display text-lg font-extrabold text-paper">{cat.label}</span>
                        <span
                          data-testid={`checklist-count-${cat.key}`}
                          className="mt-0.5 block text-xs font-bold uppercase tracking-[0.14em]"
                          style={{ color: catDone > 0 ? color : "rgba(246,243,252,0.4)" }}
                        >
                          {catDone} of {cat.items.length} in place
                        </span>
                      </span>
                      {isComplete && (
                        <span
                          data-testid={`checklist-complete-${cat.key}`}
                          className="hidden items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em] sm:flex"
                          style={{ backgroundColor: `${color}26`, color }}
                        >
                          <Check className="h-3 w-3" strokeWidth={3.5} aria-hidden="true" />
                          Complete
                        </span>
                      )}
                      <ChevronDown
                        className={`h-5 w-5 shrink-0 text-paper/50 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                        aria-hidden="true"
                      />
                    </button>
                    {isOpen && (
                      <div className="space-y-1 border-t border-white/[0.06] p-3 sm:p-4">
                        {cat.items.map((item) => (
                          <ChecklistItem
                            key={item.id}
                            item={item}
                            color={color}
                            checked={Boolean(checked[item.id])}
                            onToggle={() => toggleItem(item.id)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {proposal && (
        <section id="proposal" data-testid="proposal-section" className="scroll-mt-28 pb-20 sm:pb-24" tabIndex={-1}>
          <div className="mx-auto w-full max-w-shell px-5 sm:px-8">
            <Reveal>
              {proposal.needed.length === 0 ? (
                <div
                  data-testid="proposal-complete"
                  className="rounded-[2rem] border border-lilac/30 bg-card p-9 text-center sm:p-14"
                >
                  <PartyPopper className="mx-auto h-10 w-10 text-lilac" aria-hidden="true" />
                  <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-paper">
                    Nothing left on this list.
                  </h2>
                  <p className="mx-auto mt-3 max-w-md text-base text-paper/60">
                    You've checked every box — your brand looks launch-ready. If you'd like a final review before you
                    go live, we're glad to take a look.
                  </p>
                  <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                    <PrimaryButton testId="proposal-cta-button" onClick={() => onBuild({ need: "Launch Checklist", ...contactPrefill })}>
                      Book a final review
                    </PrimaryButton>
                    <button
                      type="button"
                      data-testid="proposal-reset-button"
                      onClick={reset}
                      className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3.5 text-sm font-bold text-paper/70 transition-[border-color,color] duration-200 hover:border-lilac/50 hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac focus-visible:ring-offset-2 focus-visible:ring-offset-ink"
                    >
                      <RotateCcw className="h-4 w-4" aria-hidden="true" />
                      Start over
                    </button>
                  </div>
                </div>
              ) : (
                <div data-testid="proposal-panel" className="overflow-hidden rounded-[2rem] border border-lilac/25 bg-card">
                  <div className="flex flex-wrap items-start justify-between gap-4 border-b border-white/[0.07] bg-lilac/[0.06] p-7 sm:p-9">
                    <div>
                      <Eyebrow testId="proposal-eyebrow">Your custom proposal</Eyebrow>
                      <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-paper">
                        What's left to launch.
                      </h2>
                      <p className="mt-2 text-sm text-paper/55">
                        Prepared for{" "}
                        <span className="font-bold text-paper">
                          {contact.businessName.trim() || contact.name.trim() || "Your brand"}
                        </span>{" "}
                        · {proposal.date}
                      </p>
                      {(contact.email.trim() || contact.phone.trim()) && (
                        <p data-testid="proposal-contact" className="mt-1 text-xs text-paper/40">
                          {[contact.email.trim(), contact.phone.trim()].filter(Boolean).join(" · ")}
                        </p>
                      )}
                    </div>
                    <p data-testid="proposal-number" className="rounded-full border border-lilac/30 px-4 py-1.5 text-xs font-bold tracking-[0.14em] text-lilac">
                      #{numberRef.current}
                    </p>
                  </div>

                  <div className="p-7 sm:p-9">
                    <div className="space-y-8">
                      {proposal.groups.map((group) => {
                        const Icon = CATEGORY_ICONS[group.key];
                        const color = CATEGORY_COLORS[group.key];
                        const groupTotal = group.neededItems.reduce((s, i) => s + i.price, 0);
                        return (
                          <div key={group.key} data-testid={`proposal-group-${group.key}`}>
                            <div className="flex items-center justify-between gap-4">
                              <div className="flex items-center gap-3">
                                <span
                                  className="flex h-9 w-9 items-center justify-center rounded-xl"
                                  style={{ backgroundColor: `${color}26`, color }}
                                >
                                  <Icon className="h-4 w-4" strokeWidth={2.4} aria-hidden="true" />
                                </span>
                                <h3 className="font-display text-lg font-extrabold text-paper">{group.label}</h3>
                              </div>
                              <span className="text-sm font-bold" style={{ color }}>
                                {money(groupTotal)}
                              </span>
                            </div>
                            <ul className="mt-4 divide-y divide-white/[0.05]">
                              {group.neededItems.map((item) => (
                                <li
                                  key={item.id}
                                  data-testid={`proposal-line-${item.id}`}
                                  className="flex items-baseline justify-between gap-4 py-2.5 text-sm"
                                >
                                  <span className="text-paper/70">{item.label}</span>
                                  <span className="shrink-0 font-bold text-paper">{money(item.price)}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-10 space-y-3 border-t border-white/10 pt-7">
                      <div className="flex items-baseline justify-between text-sm">
                        <span className="text-paper/55">Subtotal</span>
                        <span data-testid="proposal-subtotal" className="font-bold text-paper/80">
                          {money(proposal.subtotal)}
                        </span>
                      </div>
                      {proposal.discount > 0 && (
                        <div className="flex items-baseline justify-between text-sm">
                          <span className="text-paper/55">Package discount</span>
                          <span data-testid="proposal-discount" className="font-bold text-lilac">
                            −{money(proposal.discount)}
                          </span>
                        </div>
                      )}
                      {proposal.minimumApplied && (
                        <p data-testid="proposal-min-note" className="text-xs text-paper/45">
                          Minimum engagement of {money(MIN_PRICE)} applies.
                        </p>
                      )}
                      <div className="flex items-end justify-between gap-4 pt-2">
                        <span className="font-display text-lg font-extrabold text-paper">Estimated investment</span>
                        <span data-testid="proposal-total" className="font-display text-4xl font-extrabold tracking-tight text-lilac sm:text-5xl">
                          {money(proposal.totalPrice)}
                        </span>
                      </div>
                      <p data-testid="proposal-pricing-note" className="pt-1 text-xs leading-relaxed text-paper/45">
                        One-time estimate based on the boxes you didn't check, using our standard per-service rate
                        card. Package pricing is separate: Business in a Box bundles the core launch scope — brand,
                        storefront, supply setup, and fulfillment — for $9,997. Final scope and quote are confirmed
                        together — nothing is due now.
                      </p>
                    </div>

                    <div className="mt-9 flex flex-wrap items-center gap-4">
                      <PrimaryButton testId="proposal-cta-button" onClick={openInquiryWithProposal}>
                        Request a launch proposal
                      </PrimaryButton>
                      <button
                        type="button"
                        data-testid="proposal-pdf-button"
                        onClick={() =>
                          downloadProposalPdf({ proposal, number: numberRef.current, contact, money })
                        }
                        className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3.5 text-sm font-bold text-paper/70 transition-[border-color,color] duration-200 hover:border-lilac/50 hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac focus-visible:ring-offset-2 focus-visible:ring-offset-ink"
                      >
                        <Download className="h-4 w-4" aria-hidden="true" />
                        Download PDF
                      </button>
                      <button
                        type="button"
                        data-testid="proposal-reset-button"
                        onClick={reset}
                        className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3.5 text-sm font-bold text-paper/70 transition-[border-color,color] duration-200 hover:border-lilac/50 hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac focus-visible:ring-offset-2 focus-visible:ring-offset-ink"
                      >
                        <RotateCcw className="h-4 w-4" aria-hidden="true" />
                        Start over
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </Reveal>
          </div>
        </section>
      )}

      <ClosingCTA onBuild={() => onBuild()} />
    </>
  );
}
